import { useEffect, useRef, useState } from "react";
import { Camera, Download } from "lucide-react";
import api from "../lib/api";

/* Unsplash place photos with required attribution (photographer + Unsplash links,
   UTM-tagged by the backend) and download-event tracking on download-like actions. */

const memCache = new Map();

export function placePhotoQuery(name, country) {
  const label = (name || "").split("(")[0].trim();
  if (!label) return "";
  return country && !label.toLowerCase().includes(country.toLowerCase())
    ? `${label} ${country}` : label;
}

export function usePlacePhotos(query, enabled = true) {
  const [photos, setPhotos] = useState(memCache.get(query) || null);
  useEffect(() => {
    if (!enabled || !query) return;
    if (memCache.has(query)) { setPhotos(memCache.get(query)); return; }
    let alive = true;
    api.get(`/unsplash/photos?q=${encodeURIComponent(query)}`)
      .then((r) => {
        const p = r.data.photos || [];
        memCache.set(query, p);
        if (alive) setPhotos(p);
      })
      .catch(() => { if (alive) setPhotos([]); });
    return () => { alive = false; };
  }, [query, enabled]);
  return photos;
}

function trackDownload(photo) {
  if (photo.download_location) {
    api.post("/unsplash/download", { download_location: photo.download_location }).catch(() => {});
  }
  window.open(photo.image, "_blank", "noopener,noreferrer");
}

export function Attribution({ photo, tiny = false }) {
  return (
    <span className={tiny ? "text-[9px] leading-tight text-zinc-500" : "text-[11px] text-zinc-500"}>
      Photo by <a href={photo.photographer_url} target="_blank" rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="underline hover:text-zinc-800">{photo.photographer_name}</a>
      {" "}on <a href={photo.photo_url} target="_blank" rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="underline hover:text-zinc-800">Unsplash</a>
    </span>
  );
}

/* compact: 3 small thumbs (used in hover previews) */
export function PhotoPreview({ query, name }) {
  const photos = usePlacePhotos(query);
  if (photos === null) return (
    <div className="flex gap-1">{[0, 1, 2].map((i) => <div key={i} className="w-full h-16 bg-zinc-100 animate-pulse" />)}</div>
  );
  if (photos.length === 0) return <p className="text-[11px] text-zinc-400 px-1 py-2">No photos yet for {name}.</p>;
  return (
    <div data-testid="unsplash-preview">
      <div className="grid grid-cols-3 gap-1">
        {photos.map((p) => (
          <a key={p.id} href={p.photo_url} target="_blank" rel="noreferrer">
            <img src={p.thumb} alt={p.alt || name} loading="lazy" className="w-full h-16 object-cover" />
          </a>
        ))}
      </div>
      <div className="mt-1 px-0.5 flex flex-wrap gap-x-2">
        {photos.map((p) => <Attribution key={p.id} photo={p} tiny />)}
      </div>
    </div>
  );
}

/* wraps any element with a photo popover: hover (desktop) or camera-icon tap (touch).
   icon: "auto" = show tap icon only on touch devices; true = always; false = never. */
const TOUCH = typeof window !== "undefined" && window.matchMedia &&
  window.matchMedia("(hover: none)").matches;

export function HoverPhotoPreview({ query, name, sub, testId, className = "", icon = "auto", children }) {
  const [show, setShow] = useState(false);
  const ref = useRef(null);
  const timer = useRef(null);
  const enter = () => { if (!TOUCH) timer.current = setTimeout(() => setShow(true), 300); };
  const leave = () => { clearTimeout(timer.current); if (!TOUCH) setShow(false); };
  useEffect(() => {
    if (!show) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setShow(false); };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [show]);
  const showIcon = icon === true || (icon === "auto" && TOUCH);
  return (
    <span ref={ref} className={`relative inline-block ${className}`} onMouseEnter={enter} onMouseLeave={leave}>
      {children}
      {showIcon && (
        <button type="button" aria-label={`Photos of ${name}`} data-testid={testId ? `${testId}-toggle` : undefined}
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); clearTimeout(timer.current); setShow((s) => !s); }}
          className="ml-1 align-middle text-zinc-400 hover:text-blue-700 transition-colors">
          <Camera className="w-3.5 h-3.5 inline" />
        </button>
      )}
      {show && (
        <div className="absolute left-0 top-full mt-1 z-40 w-72 max-w-[80vw] bg-white border border-zinc-300 shadow-xl p-2"
          data-testid={testId}>
          <div className="text-xs font-800 mb-1.5 text-zinc-900">
            {name} {sub && <span className="text-zinc-400 font-600">· {sub}</span>}
          </div>
          <PhotoPreview query={query} name={name} />
        </div>
      )}
    </span>
  );
}

/* full gallery: used on destination guide pages */
export default function UnsplashGallery({ query, name, accent = "#1a73e8" }) {
  const photos = usePlacePhotos(query);
  if (photos === null || photos.length === 0) return null;
  return (
    <section className="mb-8" data-testid="unsplash-gallery">
      <h2 className="font-heading font-900 text-2xl border-b-2 pb-2 mb-3 flex items-center gap-2"
        style={{ borderColor: accent }}>
        <Camera className="w-6 h-6" style={{ color: accent }} /> Photos of {name}
      </h2>
      <div className="grid sm:grid-cols-3 gap-3">
        {photos.map((p) => (
          <figure key={p.id} className="bg-white border border-zinc-200 group" data-testid={`unsplash-photo-${p.id}`}>
            <div className="relative">
              <a href={p.photo_url} target="_blank" rel="noreferrer">
                <img src={p.image} alt={p.alt || name} loading="lazy" className="w-full h-44 object-cover" />
              </a>
              <button onClick={() => trackDownload(p)} title="Open full-size photo"
                data-testid={`unsplash-download-${p.id}`}
                className="absolute top-2 right-2 p-1.5 bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                <Download className="w-3.5 h-3.5" />
              </button>
            </div>
            <figcaption className="px-2 py-1.5"><Attribution photo={p} /></figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}
